import React, { useState } from 'react';
import { Send, Shield, Info, CheckCircle2, AlertCircle } from 'lucide-react';
import { COACHING_PLANS } from '../data/coachingData';
import { ConsultationLead } from '../types';
import { submitConsultationLead } from '../services/leadService';

interface ConsultationFormProps {
  initialPlanId?: string;
  initialMarketCode?: string;
  initialPricingVersionId?: string;
  onClose?: () => void;
  isModal?: boolean;
}

export const ConsultationForm: React.FC<ConsultationFormProps> = ({
  initialPlanId,
  initialMarketCode,
  initialPricingVersionId,
  onClose,
  isModal = false
}) => {
  const [renderTimestamp] = useState<number>(() => Date.now());

  const [formData, setFormData] = useState<ConsultationLead & { honeypot?: string; renderedAt?: number }>({
    fullName: '',
    email: '',
    phone: '',
    fitnessGoal: 'strength-development',
    experienceLevel: 'intermediate',
    preferredPlanId: initialPlanId || '',
    timeline: 'immediate',
    marketCode: initialMarketCode || 'US',
    pricingVersionId: initialPricingVersionId || '',
    message: '',
    honeypot: '',
    renderedAt: Date.now()
  });

  // Keep preferred plan in sync if parent updates initialPlanId
  React.useEffect(() => {
    if (initialPlanId) {
      setFormData((prev) => ({
        ...prev,
        preferredPlanId: initialPlanId,
        ...(initialMarketCode ? { marketCode: initialMarketCode } : {}),
        ...(initialPricingVersionId ? { pricingVersionId: initialPricingVersionId } : {})
      }));
    }
  }, [initialPlanId, initialMarketCode, initialPricingVersionId]);

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isPreviewSubmission, setIsPreviewSubmission] = useState(false);
  const [submittedLeadId, setSubmittedLeadId] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear specific field error on edit
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');
    setFieldErrors({});

    try {
      // Dispatch lead to secure Cloud Function backend with render timestamp
      const payload = {
        ...formData,
        renderedAt: renderTimestamp
      };
      const response = await submitConsultationLead(payload);

      if (response.success) {
        setIsPreviewSubmission(!!response.isPreview);
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(response.error || 'Failed to submit consultation. Please try again.');
        if (response.errors) {
          setFieldErrors(response.errors);
        }
      }
    } catch (err) {
      console.error('Lead submission failure:', err);
      setStatus('error');
      setErrorMessage('A network error occurred while submitting your consultation inquiry.');
    }
  };

  const handleReset = () => {
    setStatus('idle');
    setSubmittedLeadId(null);
    setErrorMessage('');
    setFieldErrors({});
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      fitnessGoal: 'strength-development',
      experienceLevel: 'intermediate',
      preferredPlanId: initialPlanId || '',
      timeline: 'immediate',
      message: '',
      honeypot: '',
      renderedAt: Date.now()
    });
  };

  const content = (
    <div className="consultation-card" style={{
      backgroundColor: 'var(--bg-surface-elevated)',
      borderRadius: 'var(--radius-lg)',
      padding: '2.5rem',
      border: '1px solid var(--border-color)',
      maxWidth: '680px',
      margin: '0 auto',
      position: 'relative'
    }}>
      {status === 'success' ? (
        <div className="consultation-success" style={{ textAlign: 'center', padding: '2rem 1rem' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: isPreviewSubmission ? 'rgba(59, 130, 246, 0.15)' : 'rgba(34, 197, 94, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem auto'
          }}>
            {isPreviewSubmission ? (
              <Info size={36} color="#60a5fa" />
            ) : (
              <CheckCircle2 size={36} color="#22c55e" />
            )}
          </div>
          <h3 style={{ fontSize: '1.6rem', marginBottom: '0.75rem', color: '#ffffff' }}>
            {isPreviewSubmission ? 'Plan Selection & Validation Verified' : 'Inquiry Verified & Queued'}
          </h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: 1.6, fontSize: '0.95rem' }}>
            {isPreviewSubmission ? (
              <>
                Thank you, <strong>{formData.fullName}</strong>. You are viewing the interactive staging preview. Your inputs have passed client validation and plan selection rules. <strong>No prospect data has been saved to a database or sent to any production backend.</strong>
              </>
            ) : (
              <>
                Thank you, <strong>{formData.fullName}</strong>. Your consultation inquiry has passed validation and has been staged with ID <code style={{ color: 'var(--accent-amber-light)' }}>{submittedLeadId}</code>.
              </>
            )}
          </p>

          <div style={{
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: 'var(--radius-md)',
            padding: '1rem',
            textAlign: 'left',
            fontSize: '0.82rem',
            color: 'var(--text-muted)',
            marginBottom: '1.75rem'
          }}>
            <div style={{ fontWeight: 700, color: '#ffffff', marginBottom: '0.4rem' }}>
              {isPreviewSubmission ? 'Validated Prospect Data (Preview Mode — No Database Write):' : 'Validated Lead Record (Segregated Collection: coachingLeads):'}
            </div>
            <div>• Mode: <span style={{ color: isPreviewSubmission ? '#60a5fa' : '#4ade80' }}>{isPreviewSubmission ? 'Staging Interactive Preview (Spark Free Plan)' : 'Production Cloud Function'}</span></div>
            <div>• Prospect Name: {formData.fullName}</div>
            <div>• Email: {formData.email}</div>
            <div>• Phone: {formData.phone}</div>
            <div>• Preferred Tier: {COACHING_PLANS.find(p => p.id === formData.preferredPlanId)?.name || formData.preferredPlanId}</div>
            <div>• Primary Goal: {formData.fitnessGoal}</div>
            <div>• Experience Level: {formData.experienceLevel}</div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
              style={{ fontSize: '0.88rem' }}
            >
              Test Another Plan
            </button>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="btn-primary"
                style={{ fontSize: '0.88rem' }}
              >
                Close Window
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <span className="badge-tag" style={{ marginBottom: '0.75rem' }}>
              Coaching Inquiry
            </span>
            <h2 style={{ fontSize: '2.2rem', marginBottom: '0.5rem' }}>
              Let’s start with you.
            </h2>
            <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)' }}>
              Share a few details. We’ll review your goals and help you find the right coaching fit.
            </p>
          </div>

          {status === 'error' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 'var(--radius-md)',
              padding: '0.85rem 1rem',
              color: '#fca5a5',
              fontSize: '0.88rem',
              marginBottom: '1.5rem'
            }}>
              <AlertCircle size={18} style={{ flexShrink: 0 }} />
              <span>{errorMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Honeypot field (hidden from real users, catches automated spam bots) */}
            <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <label htmlFor="website_hp">Leave this empty</label>
              <input
                id="website_hp"
                type="text"
                name="honeypot"
                tabIndex={-1}
                autoComplete="off"
                value={formData.honeypot || ''}
                onChange={handleChange}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
              <div>
                <label htmlFor="lead-fullname" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                  Full Name *
                </label>
                <input
                  id="lead-fullname"
                  type="text"
                  name="fullName"
                  required
                  placeholder="e.g. Alex Morgan"
                  value={formData.fullName}
                  onChange={handleChange}
                  style={{
                    ...inputStyle,
                    borderColor: fieldErrors.fullName ? 'var(--accent-red, #D4AF37)' : 'var(--border-color)'
                  }}
                />
                {fieldErrors.fullName && (
                  <div style={{ color: '#f87171', fontSize: '0.78rem', marginTop: '0.25rem' }}>{fieldErrors.fullName}</div>
                )}
              </div>

              <div>
                <label htmlFor="lead-email" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                  Email Address *
                </label>
                <input
                  id="lead-email"
                  type="email"
                  name="email"
                  required
                  placeholder="alex@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  style={{
                    ...inputStyle,
                    borderColor: fieldErrors.email ? 'var(--accent-red, #D4AF37)' : 'var(--border-color)'
                  }}
                />
                {fieldErrors.email && (
                  <div style={{ color: '#f87171', fontSize: '0.78rem', marginTop: '0.25rem' }}>{fieldErrors.email}</div>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
              <div>
                <label htmlFor="lead-phone" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                  Phone Number *
                </label>
                <input
                  id="lead-phone"
                  type="tel"
                  name="phone"
                  required
                  placeholder="+1 (555) 000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                  style={{
                    ...inputStyle,
                    borderColor: fieldErrors.phone ? 'var(--accent-red, #D4AF37)' : 'var(--border-color)'
                  }}
                />
                {fieldErrors.phone && (
                  <div style={{ color: '#f87171', fontSize: '0.78rem', marginTop: '0.25rem' }}>{fieldErrors.phone}</div>
                )}
              </div>

              <div>
                <label htmlFor="lead-preferred-plan" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                  Preferred Coaching Plan <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-muted, #94a3b8)' }}>(Optional)</span>
                </label>
                <select
                  id="lead-preferred-plan"
                  name="preferredPlanId"
                  value={formData.preferredPlanId}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="" style={{ backgroundColor: '#181f2c', color: '#94a3b8' }}>
                    Undecided / Discuss During Consultation
                  </option>
                  {COACHING_PLANS.map((plan) => (
                    <option key={plan.id} value={plan.id} style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                      {plan.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
              <div>
                <label htmlFor="lead-fitness-goal" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                  Primary Objective *
                </label>
                <select
                  id="lead-fitness-goal"
                  name="fitnessGoal"
                  value={formData.fitnessGoal}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="strength-development" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Strength Development &amp; Progressive Overload
                  </option>
                  <option value="physique-hypertrophy" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Muscle Hypertrophy &amp; Physique Conditioning
                  </option>
                  <option value="fat-loss-recomposition" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Fat Loss &amp; Body Recomposition
                  </option>
                  <option value="athletic-performance" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Athletic Conditioning &amp; Stamina
                  </option>
                  <option value="longevity-mobility" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Joint Mobility, Posture &amp; Longevity
                  </option>
                  <option value="general-fitness" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Functional Movement, Health &amp; General Wellness
                  </option>
                </select>
              </div>

              <div>
                <label htmlFor="lead-experience" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                  Training Background *
                </label>
                <select
                  id="lead-experience"
                  name="experienceLevel"
                  value={formData.experienceLevel}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="beginner" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Beginner (&lt; 1 Year Experience)
                  </option>
                  <option value="restarting-on-off" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Restarting / On &amp; Off (Past Experience, Inconsistent Currently)
                  </option>
                  <option value="intermediate" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Intermediate (1 - 3 Years Consistent Training)
                  </option>
                  <option value="advanced" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Advanced (3+ Years Systematic Training)
                  </option>
                  <option value="returning-break" style={{ backgroundColor: '#181f2c', color: '#fff' }}>
                    Returning After Extended Break / Injury Recovery
                  </option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="lead-message" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                Additional Notes or Availability (Optional):
              </label>
              <textarea
                id="lead-message"
                name="message"
                rows={3}
                placeholder="Preferred consultation times, general questions, or background..."
                value={formData.message}
                onChange={handleChange}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              <Shield size={16} color="var(--accent-amber)" />
              <span>Prospect information is kept confidential. Medical intake and PAR-Q are managed through the client app upon enrollment.</span>
            </div>

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="btn-primary"
              style={{
                width: '100%',
                padding: '1.05rem',
                fontSize: '1rem',
                marginTop: '0.5rem',
                opacity: status === 'submitting' ? 0.7 : 1
              }}
            >
              {status === 'submitting' ? 'Validating & Submitting...' : 'Submit Consultation Request'} <Send size={18} />
            </button>
          </form>
        </>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div className="consultation-modal light-section" style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        overflowY: 'auto'
      }}>
        <div style={{ width: '100%', maxWidth: '720px', position: 'relative' }}>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '1.5rem',
                right: '1.5rem',
                zIndex: 10,
                color: 'var(--text-secondary)',
                fontSize: '1.5rem',
                padding: '0.5rem',
                lineHeight: 1,
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
              aria-label="Close modal"
            >
              &times;
            </button>
          )}
          {content}
        </div>
      </div>
    );
  }

  return (
    <section id="consultation" className="light-section consultation-editorial">
      <div className="container consultation-layout">
        <div className="consultation-intro"><p className="eyebrow">YOUR NEXT STEP</p><h2>It starts with<br /><em>a conversation.</em></h2><p>You don’t need to have everything figured out. Tell us where you are now and what you’d like to work toward.</p><ol><li><span>01</span>Share your goals and preferred plan.</li><li><span>02</span>We review your request.</li><li><span>03</span>We arrange an introductory consultation.</li></ol><a href="#plans">Want to compare plans first? ↗</a></div>
        {content}
      </div>
    </section>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  backgroundColor: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-sm)',
  padding: '0.75rem 1rem',
  fontSize: '0.92rem',
  color: 'var(--text-primary)',
  outline: 'none',
  transition: 'border-color 0.2s',
  fontFamily: 'inherit'
};
